<template>
  <card-container
    :id="pageId"
    :is-loading="isLoading"
    :disabled="isLoading"
    loading-message="Sending Message..."
    @submit="submitForm"
    @reset="resetForm"
  >
    <template #title>
      Text Delivery Driver
    </template>

    <template #content>
      <div class="row align-items-center">
        <label
          class="col-sm-3 control-label"
          for="delivery-driver-number"
        >Delivery Driver Number (TO)*</label>
        <div class="col-sm-7">
          <sanitized-input
            id="delivery-driver-number"
            v-model="requestBody.driverNumber"
            maxlength="11"
            required
          />
        </div>
      </div>

      <div class="row align-items-center">
        <label
          class="col-sm-3 control-label"
          for="operator-number"
        >Operator Account Number (FROM)*</label>
        <div class="col-sm-7">
          <sanitized-input
            id="operator-number"
            v-model="requestBody.operatorNumber"
            required
          />
        </div>
      </div>

      <div class="row align-items-center">
        <label
          class="col-sm-3 control-label"
          for="customer-number"
        >Customer Number*</label>
        <div class="col-sm-7">
          <sanitized-input
            id="customer-number"
            v-model="requestBody.phoneNumber"
            required
          />
        </div>
      </div>

      <div class="row align-items-center">
        <label
          class="col-sm-3 control-label"
          for="delivery-address"
        >Delivery Address*</label>
        <div class="col-sm-7">
          <sanitized-input
            id="delivery-address"
            v-model="requestBody.deliveryAddress"
            required
          />
        </div>
      </div>

      <div class="row align-items-center">
        <label
          class="col-sm-3 control-label"
          for="additional-directions"
        >Additional Directions</label>
        <div class="col-sm-7">
          <sanitized-input
            id="additional-directions"
            v-model="requestBody.additionalDirections"
            @keyup="updateMessageText"
          />
        </div>
      </div>

      <div class="row align-items-center">
        <label
          class="col-sm-3 control-label"
          for="additional-instructions"
        >Additional Instructions</label>
        <div class="col-sm-7">
          <sanitized-input
            id="additional-instructions"
            v-model="requestBody.additionalInstructions"
            @keyup="updateMessageText"
          />
        </div>
      </div>

      <div class="row align-items-center">
        <label
          class="col-sm-3 control-label"
          for="text-message-1"
        >#1 Message Text</label>
        <div class="col-sm-7">
          <sanitized-textarea
            id="text-message-1"
            v-model="requestBody.textMessage1"
            placeholder="Your message to the delivery driver"
            autosize
            required
          />
        </div>
      </div>

      <div
        v-show="requestBody.textMessage2"
        class="row align-items-center"
      >
        <label
          class="col-sm-3 control-label"
          for="text-message-2"
        >#2 Message Text</label>
        <div class="col-sm-7">
          <sanitized-textarea
            id="text-message-2"
            v-model="requestBody.textMessage2"
            placeholder="Your message to the delivery driver"
            autosize
          />
        </div>
      </div>
    </template>

    <template #submit-button>
      Send Messages
    </template>
  </card-container>
</template>

<script lang="ts">
import { reactive, watch, ref } from 'vue';
import { useRoute } from 'vue-router';

import util from '@/Utils/util';
import { useCustomer } from '@/use/customer';
import { useSwalDialog } from '@/use/swalDialog';
import { gourmetService } from '@/services/gourmet';

const pageId = 'text-delivery-driver';

export default {
  name: 'DeliverySMS',

  setup: () => {
    const route = useRoute();

    const { customerInfo } = useCustomer();
    const { showSuccessPopupDialog, showErrorPopupDialog } = useSwalDialog();

    const requestBody = reactive({
      phoneNumber: '',
      operatorNumber: '',
      driverNumber: '',
      deliveryAddress: '',
      additionalDirections: '',
      additionalInstructions: '',
      textMessage1: '',
      textMessage2: '',
    });
    const isLoading = ref(false);

    const updateMessageText = () => {
      requestBody.textMessage2 = `${requestBody.additionalDirections ? `Directions: ${requestBody.additionalDirections}. ` : ''}${
        requestBody.additionalInstructions ? `Additional: ${requestBody.additionalInstructions}` : ''
      }`;
    };

    const submitForm = async () => {
      isLoading.value = true;

      const messages = [requestBody.textMessage1];

      if (requestBody.textMessage2) {
        messages.push(requestBody.textMessage2);
      }

      try {
        await gourmetService.sendSMSMessages({
          messages,
          fromNumber: requestBody.operatorNumber,
          toNumber: requestBody.driverNumber,
        });

        showSuccessPopupDialog({
          text: 'Your message(s) have been sent successfully.',
        });
      } catch (error) {
        const errorText = util.getGraphqlError(error, false);
        showErrorPopupDialog({
          text: errorText,
        });
      }
      isLoading.value = false;
    };

    const resetForm = () => {
      const deliveryAddress = (route.query.deliveryAddress as string) || '';
      const customerAddress = customerInfo.value?.streetAddress
        ? `${customerInfo.value.streetAddress}, ${customerInfo.value.city}, ${customerInfo.value.state}, ${customerInfo.value.country} ${customerInfo.value.zip}`
        : '';

      requestBody.phoneNumber = (route.query.phoneNumber as string) || customerInfo.value?.phoneNumber || '';
      requestBody.driverNumber = (route.query.driverNumber as string) || '';
      requestBody.operatorNumber = (route.query.operatorNumber as string) || '';
      requestBody.deliveryAddress = deliveryAddress || customerAddress;
      requestBody.textMessage1 = `Hi, if there are any issues with the delivery to ${requestBody.deliveryAddress}, please call the customer at ${requestBody.phoneNumber} or GoGoGrandparent at 1-855-754-5328.`;
    };

    watch(customerInfo, resetForm, {
      immediate: true,
    });

    return {
      pageId,
      requestBody,
      isLoading,

      updateMessageText,
      submitForm,
      resetForm,
    };
  },
};
</script>
